import React, { useMemo } from 'react';

// DEPENDENCIES
import clsx from 'clsx';
import toString from 'lodash/toString';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { Row, Col } from 'react-bootstrap';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints.js';
import { DASHBOARD, INTERFACE, SETTINGS } from 'pathnames.js';
import { DATE_FORMATS } from 'globals.js';

// GLOBAL FUNCTIONS
import { makePath } from 'functions.js';

// HELPERS
import { getListData } from 'helpers/getListData';
import useRecordView from 'helpers/getRecordView';
import { getReportData, useReport } from './helpers/getReportData';

// GLOBAL PARTS
import Main from 'parts/main/Main';
import Page from 'parts/page/Page';
import Preloader from 'parts/preloader/Preloader';

// LOCAL PARTS
import DataChart from './parts/DataChart';
import DataTable from './parts/DataTable';
import DetailsTable from './parts/DetailsTable';

// LOCAL TOOLS
import ChartSidebar from './tools/ChartSidebar';
import EmailSidebar from './tools/EmailSidebar';
import ExcelButton from './tools/ExcelButton';
import FavoritesButton from './tools/FavoritesButton';
import ReportFilters from './tools/ReportFilters';

// STYLES
import styles from './report.module.scss';

// MAIN COMPONENT
const Report = () => {

  // REDUX
  const { mobile } = useSelector(({ mobile }) => mobile);

  // REACT ROUTER
  const location = useLocation();

  // HOOKS
  const { isAdmin } = useRecordView() || {};
  const {
    charts,
    date_refreshed,
    reportCode,
    reportID,
    reportKey,
    endpoint,
    isCustom,
    isLoading,
    isError,
    errorMessage,
    filters,
    updateFilter,
    report_title,
    report_description,
    data: { show_filter = true } = {},
  } = useReport();

  // MEMOS
  const showDetails = useMemo(
    () => location.pathname.split('/').filter(i => !!i).at(-1) === DASHBOARD.list,
    [location]
  )

  const { startDate, endDate } = filters || {};
  // MEMOS
  const detailsArgs = useMemo(
    () => ({
      endpoint: isCustom ? ENDPOINTS.dashboard.custom.details(reportCode) : ENDPOINTS.dashboard.report.details(reportID),
      // Need to pass in startDate and endDate since they now reside on preference 'report' root
      params: { ...(location?.state?.queryParams || {}), startDate, endDate }
    }),
    [isCustom, reportCode, reportID, location, startDate, endDate]
  )
  const DetailsMemo = useMemo(
    () => getListData(DetailsTable, {
      key: reportKey,
      fetchArgs: detailsArgs
    }),
    [detailsArgs, reportKey]
  )
  const buttonProps = useMemo(
    () => mobile ? {
      label: undefined,
      size: undefined,
      square: true
    } : {},
    [mobile]
  )
  const multiChart = useMemo(
    () => charts.length > 1,
    [charts]
  )

  // Either Reports - Graphs or My Favorites
  const dashboardPath = location.pathname.split(DASHBOARD.report)[0];

  // RENDER
  return (<>
    <Main>
      <Preloader loading={isLoading}>
        <Page.Header
          title={isLoading ? (
            <span className="font-italic text-muted">Loading...</span>
          ) : isError ? (
            <span className="text-danger text-uppercase">{errorMessage}</span>
          ) : report_title}
          headerTools={<div className={clsx('mt-3', styles.description)}>{report_description}</div>}
          backPathname={showDetails ? makePath(location.pathname.split(DASHBOARD.list)[0]) : isAdmin ? makePath(INTERFACE.settings, SETTINGS.reports) : makePath(dashboardPath)}
          headerRight={<ExcelButton {...(showDetails ? detailsArgs : {endpoint})} />}
        />
        {!showDetails &&
          <div className={styles.tools}>
            {show_filter &&
              <ReportFilters className={styles.filters} filters={filters} updateFilter={updateFilter}>
                <ReportFilters.Date />
                <ReportFilters.Button.SalesType name="saleType" />
                <ReportFilters.Button.NewUsed />
              </ReportFilters>
            }
            {!isCustom && (<>
              <FavoritesButton
                disabled={isLoading || isError}
                {...buttonProps}
              />
              <EmailSidebar.Button
                disabled={isLoading || isError}
                {...buttonProps}
              />
              {isAdmin &&
                <ChartSidebar.Button
                  disabled={isLoading || isError}
                  {...buttonProps}
                />
              }
            </>)}
          </div>
        }
        {!!toString(date_refreshed)?.trim() ? (
          <span className="mb-3" style={{ fontSize: 13 }}>
            Graph Last Refreshed: {format(new Date(date_refreshed), DATE_FORMATS.dateTime)}
          </span>
        ) : null}
        {isError ? null : showDetails ? <DetailsMemo /> : (
          <div className={styles.container}>
            <Row className="justify-content-center">
              {charts.map((p, i) => (
                <Col key={i} xs={24} xl={multiChart && (charts.length % 2 === 0 || charts.length !== i + 1) ? 12 : 24}>
                  <DataChart showTitle={multiChart} {...p} />
                </Col>
              ))}
            </Row>
            <DataTable />
          </div>
        )}
      </Preloader>
    </Main>
    <EmailSidebar />
    {isAdmin &&
      <ChartSidebar />
    }
  </>);
};

// EXPORT
export default getReportData(Report);
